import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { getInstituteHandOutStatCards } from '@/utils/handOutStatCards';
import {
  distributionInstitutesQueryOptions,
  distributionMembersQueryOptions,
} from '../api/product.api';
import { getDistributionInstituteColumns } from '../components/ProductColumns';
import { toDistributionMemberParams } from '../utils/productFilters';

const route = getRouteApi('/_auth/product/specimen-distribution/$specimenId/$zmId');

const UNKNOWN_ZM_TITLE = 'ZM';

const INSTITUTE_COLUMNS = getDistributionInstituteColumns();

interface SpecimenZmInstitutesPageProps {
  specimenId: number;
  zmId: number;
}

export function SpecimenZmInstitutesPage({ specimenId, zmId }: SpecimenZmInstitutesPageProps) {
  const zmSearch = route.useSearch();
  const navigate = route.useNavigate();
  const memberParams = toDistributionMemberParams(zmSearch);

  useRememberViewedRow('specimenZms', zmId, specimenId);

  const zmsQuery = useQuery(distributionMembersQueryOptions(specimenId, memberParams));
  const institutesQuery = useQuery(
    distributionInstitutesQueryOptions(specimenId, zmId, memberParams),
  );

  const zm = zmsQuery.data?.find((member) => member.userId === zmId);
  const title = zmsQuery.isLoading ? undefined : (zm?.name ?? UNKNOWN_ZM_TITLE);

  return (
    <DetailTableLayout
      title={title}
      titleAddon={<DateRangeBadge dateFrom={zmSearch.dateFrom} dateTo={zmSearch.dateTo} />}
      onNoHistory={() =>
        void navigate({
          to: '/product/specimen-distribution/$specimenId',
          params: { specimenId },
          search: zmSearch,
        })
      }
      stats={getInstituteHandOutStatCards(institutesQuery.data)}
      table={{
        rows: institutesQuery.data ?? [],
        columns: INSTITUTE_COLUMNS,
        isLoading: institutesQuery.isLoading,
        error: institutesQuery.error,
        errorTitle: 'Could not load the institutes',
        onRetry: () => void institutesQuery.refetch(),
        stickyColumnId: 'instituteName',
        emptyMessage: 'No institute entry carries this specimen',
      }}
    />
  );
}
